/**
 * Tag Normalization Utility (Frontend)
 * Auto-translates tag variants to canonical forms while preserving original text
 */

interface NormalizationRule {
  canonical: string;
  exactMatches: string[];
  patterns: RegExp[];
}

const NORMALIZATION_RULES: NormalizationRule[] = [
  {
    canonical: 'USPS',
    exactMatches: [
      'USPS', 'US MAIL', 'US POSTAL SERVICE', 'UNITED STATES POSTAL SERVICE',
      'US POST OFFICE', 'US POSTAL', 'U.S. MAIL', 'U.S. POSTAL SERVICE',
      'POST OFFICE', 'USPS MAIL', 'POSTAL SERVICE', 'UNITED STATES POST OFFICE',
      'U.S POSTAL SERVICE', 'US POSTAL SERVICES', 'U S POSTAL', 'U S POSTAL SERVICE',
      'US POSTAL OFFICE', 'U.S. POST OFFICE', 'USPS US GOV', 'UNITED STATE POSTAL SERVICE',
      'U.S.MAIL', 'POSTAL', 'U.S. POSTAL', 'UNITED STATES POSTAL SERVICES',
      'U. S. POSTAL SERVICE', 'US POSTAL SVC', 'US MAIL SERVICE', 'U. S . POSTAL SERVICE',
      'U.S.POSTAL SERVICE', 'UNITED STATES POSTAL', 'UNITES STATES POSTAL SERVICE',
      'US POSTAL MAIL', 'US. POSTAL SERVICE', 'USPOSTAL', 'U S POST OFFICE',
      'U.S. POSTAL SERVICES', 'UNITED STATE POST OFFICE', 'UNITED STATES POSTAL SERVICE.',
      'US MAILER', 'U.S. MAILER', 'DELIVERED AMAZON TO USPS', 'AMAZON TO USPS'
    ],
    patterns: [
      /^USPS[\s\-\/]/i,  // Starts with USPS followed by space, dash, or slash
      /^FA[0-9][A-Z0-9]{0,4}$/i  // FA contractor codes like FA2A1, FA28
    ]
  }
  // Add more rules here for other shippers (UPS, FedEx, Amazon, etc.)
];

/**
 * Get canonical tag for a given tag name
 * @param tagName - Original tag name
 * @returns Canonical tag name or null if no normalization rule matches
 */
export function getCanonicalTag(tagName: string): string | null {
  if (!tagName) return null;
  
  const upperTag = tagName.toUpperCase().trim();
  
  for (const rule of NORMALIZATION_RULES) {
    // Check exact matches
    if (rule.exactMatches.includes(upperTag)) {
      return rule.canonical;
    }
    
    // Check patterns
    for (const pattern of rule.patterns) {
      if (pattern.test(tagName)) {
        return rule.canonical;
      }
    }
  }
  
  return null;
}

/**
 * Format tag for display: "CANONICAL | original" or just "original"
 * @param tagName - Original tag name
 * @param canonicalTag - Canonical tag (optional, will be computed if not provided)
 * @returns Formatted display string
 */
export function formatTagDisplay(tagName: string, canonicalTag?: string | null): string {
  const canonical = canonicalTag ?? getCanonicalTag(tagName);
  
  if (canonical && canonical.toUpperCase() !== tagName.toUpperCase().trim()) {
    return `${canonical} | ${tagName}`;
  }
  
  return tagName;
}

/**
 * Get all known canonical tags
 * @returns Array of canonical tag names
 */
export function getKnownCanonicalTags(): string[] {
  return NORMALIZATION_RULES.map(rule => rule.canonical);
}

export { NORMALIZATION_RULES };
